<script setup lang="ts">
import { computed } from 'vue';
import type { GdprPlan } from './types';
import { annualSavings, currencySymbol } from './checkout';
import PrototypeIcon from './PrototypeIcon.vue';

const props = withDefaults(defineProps<{
    plan: GdprPlan;
    compact?: boolean;
}>(), {
    compact: false,
});

const symbol = computed(() => currencySymbol(props.plan.currency));
const savings = computed(() => annualSavings(props.plan));
const monthlyEquivalent = computed(() => Math.round((props.plan.annualPrice / 12) * 10) / 10);
const annualPriceSummary = computed(() => props.plan.annualPriceFormat.replace('{amount}', `${symbol.value}${props.plan.annualPrice}`));
const annualSavingsSummary = computed(() => props.plan.annualSavingsFormat.replace('{amount}', `${symbol.value}${savings.value}`));
const annualEquivalentSummary = computed(() => props.plan.annualEquivalentFormat.replace('{amount}', `${symbol.value}${monthlyEquivalent.value}`));
</script>

<template>
    <article class="gdpr-plan-card" :class="{ 'is-compact': compact }">
        <header class="gdpr-plan-card__header">
            <div>
                <div class="gdpr-pill gdpr-plan-card__badge">
                    <PrototypeIcon name="shield" />
                    <span>{{ plan.recommendedBadgeLabel }}</span>
                </div>
                <h2 class="gdpr-plan-card__title">{{ plan.name }}</h2>
                <p v-if="!compact" class="gdpr-plan-card__tagline">{{ plan.tagline }}</p>
            </div>
        </header>

        <div class="gdpr-plan-card__price">
            <span>{{ symbol }}{{ plan.monthlyPrice }}</span>
            <small>{{ plan.monthlyPriceSuffix }}</small>
        </div>

        <p class="gdpr-plan-card__meta">
            {{ annualPriceSummary }}
            <template v-if="savings > 0">
                · {{ annualSavingsSummary }}
            </template>
            · {{ annualEquivalentSummary }}
        </p>

        <ul class="gdpr-plan-card__features">
            <li v-for="feature in plan.features" :key="feature">
                <PrototypeIcon name="check" />
                <span>{{ feature }}</span>
            </li>
        </ul>

        <div v-if="$slots.action" class="gdpr-plan-card__action">
            <slot name="action" />
        </div>
    </article>
</template>
